/**
 * Book First Class - High-Converting Landing Page
 * Mobile-first, ultra-simple, conversion-optimized
 * CMS-editable content with aggressive caching
 */
import React, { useState, useEffect, memo, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGTM, pushCTAEvent } from '../utils/gtm';
import { format } from 'date-fns';
import { useSectionOrder } from '../hooks/useSectionOrder';
import { BOOK_FIRST_CLASS_SECTIONS, LP_LAYOUT_KEYS } from '../config/lpSections';
import { 
  Star, 
  Check, 
  Clock, 
  MapPin, 
  Phone, 
  Users,
  Heart,
  Sparkles,
  Shield,
  ArrowRight,
  Loader2,
  ChevronDown,
  MessageCircle,
  ExternalLink,
  Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toIST, formatForAPI } from '../lib/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper: detect YouTube URL and return embed URL, or null if not YouTube
function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  let videoId = null;
  if (url.includes('youtube.com/watch')) {
    videoId = new URLSearchParams(new URL(url).search).get('v');
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
  } else if (url.includes('youtube.com/shorts/')) {
    videoId = url.split('shorts/')[1]?.split(/[?&#]/)[0];
  } else if (url.includes('youtube.com/embed/')) {
    return url; // already an embed URL
  }
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
}

// Renders media_url as either YouTube iframe or image
function CmsMedia({ src, alt, className = "w-full h-48 object-cover", iframeClassName = "w-full aspect-video rounded-xl", zoom }) {
  if (!src) return null;
  const embedUrl = getYouTubeEmbedUrl(src);
  if (embedUrl) {
    return (
      <iframe src={embedUrl} title={alt || 'Video'} className={iframeClassName}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen loading="lazy" />
    );
  }
  const scale = zoom && zoom !== 100 ? zoom / 100 : null;
  return <img src={src} alt={alt || ''} className={className} loading="lazy"
    style={scale ? { transform: `scale(${scale})`, transformOrigin: 'center center' } : undefined} />;
}

// ===========================================
// PERFORMANCE: Default values for instant render
// ===========================================
const DEFAULT_CMS = {
  hero_headline: { title: 'Feel Light, Flexible and Stress-Free', subtitle: "Even If You Haven't Worked Out in Years", description: "Women's Aerial Yoga for Beginners" },
  hero_image: { media_url: 'https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=1200' },
  hero_social_proof: { title: '4.8+', subtitle: '1000+ happy members' },
  hero_trust_quote: { title: 'Beginner friendly. Not intimidating at all.' },
  pricing: { title: '1197', subtitle: '2000', description: '40% OFF', metadata: { offer_text: 'LIMITED OFFER', offer_subtext: 'Only for first 50 women' } },
  whatsapp: { title: '917075969111', subtitle: 'Hi, I want to book my first class' },
  phone: { title: '+91 98765 43210' },
  problem_section: { title: 'If your body feels tight, tired or stressed…', subtitle: "You don't need a harder workout", description: 'You need something your body responds to' },
  problem_list: { title: 'Back and neck pain|Low energy throughout the day|Constant stress and tension|Keep delaying starting fitness' },
  solution_section: { title: 'In Just 1 Session, You Will:', subtitle: 'Designed to make you feel better from day one' },
  solution_list: { title: 'Stretch deeply without strain|Feel lighter and more relaxed|Improve flexibility and posture|Release stress naturally' },
  beginner_section: { title: 'Made for Complete Beginners', subtitle: 'No pressure. No judgment.' },
  beginner_list: { title: 'No experience needed|Step-by-step guidance|Safe and supportive environment' },
  method_section: { title: 'What Makes This Different' },
  method_list: { title: 'Aerial hammock support|Gentle movements|Breath-based relaxation' },
  testimonials_heading: { title: 'What Women Say' },
  testimonial_1: { title: 'My back pain reduced immediately after the first session.', subtitle: 'Priya S.', metadata: { rating: 5 } },
  testimonial_2: { title: 'I was nervous but it felt surprisingly easy and relaxing.', subtitle: 'Anjali R.', metadata: { rating: 5 } },
  testimonial_3: { title: 'I can actually stick to this. Best decision I made!', subtitle: 'Meera K.', metadata: { rating: 5 } },
  testimonial_4: { title: 'I felt lighter in just one class. Amazing experience!', subtitle: 'Divya M.', metadata: { rating: 5 } },
  value_heading: { title: 'What You Get' },
  value_list: { title: '1 guided session (60 mins)|Personal attention|Women-only environment|Stress relief guaranteed' },
  location: { title: 'Near You', subtitle: 'SAMA Studio', description: 'Just minutes away' },
  final_cta: { title: 'Start with just one class', subtitle: 'No commitment|No pressure|Just show up', description: 'Limited spots available.' },
};

const DEFAULT_LOGO = 'https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/logos/SAMA_Logo_Primary.png';

// ===========================================
// PERFORMANCE: localStorage cache helpers (per-variant)
// ===========================================
const CACHE_PREFIX = 'sama_lp_cms_';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCachedVariant = (variantKey) => {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${variantKey}`);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL) {
        return data;
      }
    }
  } catch (e) {}
  return null;
};

const setCachedVariant = (variantKey, data) => {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${variantKey}`, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (e) {}
};

// ===========================================
// CMS Data Hook - Loads variant directly, no default flash
// ===========================================
function useLandingPageCMS(studioSlug, categorySlug) {
  const key = studioSlug && categorySlug 
    ? `${studioSlug}_${categorySlug}`.toLowerCase() 
    : 'default';

  // Check variant cache synchronously for instant render
  const initialCache = React.useMemo(() => getCachedVariant(key), [key]);
  const [cmsData, setCmsData] = useState(() => initialCache || DEFAULT_CMS);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [loading, setLoading] = useState(!initialCache && key !== 'default');
  const [pageKey, setPageKey] = useState(key);

  useEffect(() => {
    setPageKey(key);

    // Re-check cache (may have updated since initial render)
    const cached = getCachedVariant(key);
    if (cached) {
      setCmsData(cached);
      setLoading(false);
      return;
    }

    // For non-default variants without cache, show loading
    if (key !== 'default') {
      setLoading(true);
    }

    // Fetch from API — only landing_page + logos sections (not all 497 items)
    const fetchCMS = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/cms/content/public?sections=landing_page,logos`);
        if (res.ok) {
          const data = await res.json();
          const processed = processData(data, key);
          setCmsData(processed.cms);
          if (processed.logo) setLogoUrl(processed.logo);
          // Cache the processed variant data
          setCachedVariant(key, processed.cms);
        }
      } catch (err) {
        console.error('CMS fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCMS();
  }, [key]);

  return { cmsData, logoUrl, loading, pageKey };
}

// Process raw CMS data into variant-specific indexed object
function processData(data, key) {
  const landingItems = data.filter(item => item.section === 'landing_page');
  const indexed = { ...DEFAULT_CMS };
  
  // Layer 1: Load "default" CMS items as the base (overrides hardcoded DEFAULT_CMS)
  landingItems.forEach(item => {
    const itemKey = item.metadata?.key;
    const itemPageKey = item.metadata?.page_key;
    if (itemKey && (itemPageKey === 'default' || !itemPageKey)) {
      indexed[itemKey] = item;
    }
  });
  
  // Layer 2: Overlay variant-specific items (overrides default CMS items)
  if (key !== 'default') {
    landingItems.forEach(item => {
      const itemKey = item.metadata?.key;
      const itemPageKey = item.metadata?.page_key;
      if (itemKey && itemPageKey === key) {
        indexed[itemKey] = item;
      }
    });
  }
  
  // Get logo
  const logoItem = data.find(item => 
    item.section === 'logos' && item.metadata?.key === 'primary_logo'
  );
  
  return { cms: indexed, logo: logoItem?.media_url || null };
}

// Helper to get CMS value with fallback
const getCMS = (cmsData, key, field = 'title', fallback = '') => {
  return cmsData[key]?.[field] || fallback;
};

// Helper to get image zoom from CMS metadata
const getZoom = (cmsData, key) => cmsData[key]?.metadata?.image_zoom || 100;

// Helper to split pipe-delimited list
const getList = (cmsData, key, fallback = []) => {
  const val = cmsData[key]?.title;
  if (!val) return fallback;
  return val.split('|').filter(Boolean);
};

// WhatsApp Icon Component (Official Brand SVG)
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Logo Header - Fixed at top (Memoized) - NO NAVIGATION, just branding
const LogoHeader = memo(function LogoHeader({ logoUrl }) {
  const defaultLogo = 'https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/logos/SAMA_Logo_Primary.png';
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
      <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-center">
        {/* Logo as static branding - no navigation to keep users on landing page */}
        <img 
          src={logoUrl || defaultLogo}
          alt="SAMA Studio"
          className="h-10 md:h-12 w-auto"
          loading="eager"
        />
      </div>
    </header>
  );
});

// Hero Section (Memoized)
const HeroSection = memo(function HeroSection({ onBookClick, cmsData, abVariant }) {
  // Get CMS values with fallbacks
  const headline = getCMS(cmsData, 'hero_headline', 'title', 'Feel Light, Flexible and Stress-Free');
  const subheadline = getCMS(cmsData, 'hero_headline', 'subtitle', "Even If You Haven't Worked Out in Years");
  const locationText = getCMS(cmsData, 'hero_headline', 'description', "Women's Aerial Yoga for Beginners in Hyderabad");
  const heroImage = getCMS(cmsData, 'hero_image', 'media_url', 'https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=1200');
  const socialProofRating = getCMS(cmsData, 'hero_social_proof', 'title', '4.8+');
  const socialProofText = getCMS(cmsData, 'hero_social_proof', 'subtitle', '1000+ happy members');
  const trustQuote = getCMS(cmsData, 'hero_trust_quote', 'title', 'Beginner friendly. Not intimidating at all.');
  
  // Pricing
  const pricing = cmsData['pricing'] || {};
  const price = pricing.title || '1197';
  const originalPrice = pricing.subtitle || '2000';
  const discount = pricing.description || '40% OFF';
  const offerText = pricing.metadata?.offer_text || 'LIMITED OFFER';
  const offerSubtext = pricing.metadata?.offer_subtext || 'Only for first 50 women';
  
  // WhatsApp - trim and sanitize number to avoid URL encoding issues
  const whatsappNumber = getCMS(cmsData, 'whatsapp', 'title', '917075969111').trim().replace(/\D/g, '');
  const whatsappMessage = encodeURIComponent(getCMS(cmsData, 'whatsapp', 'subtitle', 'Hi, I want to book my first aerial yoga class'));
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
  
  // "Know More" WhatsApp (Variant B)
  const knowMoreNumber = getCMS(cmsData, 'whatsapp_knowmore', 'title', whatsappNumber).trim().replace(/\D/g, '');
  const knowMoreMessage = encodeURIComponent(getCMS(cmsData, 'whatsapp_knowmore', 'subtitle', 'Hi, I want to know more about this class'));
  const knowMoreUrl = `https://wa.me/${knowMoreNumber}?text=${knowMoreMessage}`;
  
  const isVariantB = abVariant === 'B';

  return (
    <section className="relative min-h-[90vh] flex items-center">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: `url(${heroImage})`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 w-full px-4 py-8 md:py-16">
        <div className="max-w-lg mx-auto">
          {/* Social Proof Badge */}
          <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-2 mb-6 shadow-lg">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              <span className="font-bold text-stone-900">{socialProofRating}</span>
            </div>
            <span className="text-stone-400">•</span>
            <span className="text-stone-600 text-sm">{socialProofText}</span>
          </div>
          
          {/* Headline */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight mb-4">
            {headline}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-2">
            {subheadline}
          </p>
          <p className="text-white/70 mb-6">
            {locationText}
          </p>
          
          {/* Trust Quote */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
            <p className="text-white/90 italic text-sm">
              "{trustQuote}"
            </p>
            <div className="flex items-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
              ))}
            </div>
          </div>
          
          {/* Offer Card */}
          <div className="bg-white rounded-2xl p-5 shadow-2xl mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                {offerText}
              </span>
              <span className="text-stone-500 text-sm">{offerSubtext}</span>
            </div>
            
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-4xl font-bold text-primary">₹{price}</span>
              <span className="text-xl text-stone-400 line-through">₹{originalPrice}</span>
              <span className="bg-green-100 text-green-700 text-sm font-medium px-2 py-1 rounded">
                {discount}
              </span>
            </div>
            
            {isVariantB ? (
              <>
                <button 
                  onClick={() => { pushCTAEvent('hero'); window.open(whatsappUrl, '_blank'); }}
                  className="w-full h-14 flex items-center justify-center gap-2 text-white font-semibold rounded-xl shadow-lg shadow-green-500/30 transition-colors"
                  style={{ backgroundColor: '#25D366' }}
                  data-testid="hero-whatsapp-primary"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  Chat on WhatsApp
                </button>
                
                <button 
                  onClick={() => { pushCTAEvent('hero'); window.open(knowMoreUrl, '_blank'); }}
                  className="w-full mt-3 h-12 flex items-center justify-center gap-2 border-2 border-stone-200 text-stone-700 font-medium hover:bg-stone-50 rounded-xl transition-colors"
                  data-testid="hero-whatsapp-secondary"
                >
                  <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
                  Know More
                </button>
              </>
            ) : (
              <>
                <Button 
                  onClick={onBookClick}
                  className="w-full h-14 text-lg font-semibold rounded-xl bg-primary hover:bg-primary/90 shadow-lg shadow-primary/30"
                  data-testid="hero-book-cta"
                >
                  Book Your First Class
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                <button 
                  onClick={() => { pushCTAEvent('hero'); window.open(whatsappUrl, '_blank'); }}
                  className="w-full mt-3 h-12 flex items-center justify-center gap-2 text-green-600 font-medium hover:bg-green-50 rounded-xl transition-colors"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  Chat on WhatsApp
                </button>
              </>
            )}
            
            <p className="text-center text-stone-500 text-xs mt-3">
              Instant booking • Limited seats per slot
            </p>
          </div>
        </div>
      </div>
    </section>
  );
});

// Urgency Section - Available Slots Widget (contained booking journey) (Memoized)
const UrgencySection = memo(function UrgencySection({ studioSlug, categorySlug, onSelectSlot, isLoading, slots }) {
  return (
    <section className="py-12 px-4 bg-amber-50">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Clock className="w-5 h-5 text-amber-600" />
          <h2 className="text-xl font-bold text-stone-900">Limited Slots This Week</h2>
        </div>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
          </div>
        ) : slots.length === 0 ? (
          <div className="text-center py-8 text-stone-600">
            <p>No slots available this week. Check back soon!</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 mb-6">
              {slots.map((slot, index) => (
                <div 
                  key={slot.id || index}
                  className="bg-white rounded-xl p-4 flex items-center justify-between shadow-sm border border-amber-100 hover:border-emerald-300 transition-colors"
                >
                  <div>
                    <p className="font-semibold text-stone-900">
                      {slot.dayName} – {slot.time}
                    </p>
                    <p className="text-xs text-stone-500">{slot.className}</p>
                    <p className={`text-sm font-medium mt-1 ${slot.displaySeatsLeft <= 3 ? 'text-red-600' : 'text-emerald-600'}`}>
                      <Users className="w-3.5 h-3.5 inline mr-1" />
                      {slot.displaySeatsLeft}/{slot.capacity} left
                    </p>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => onSelectSlot(slot)}
                    className="bg-emerald-700 hover:bg-emerald-800"
                  >
                    Book
                  </Button>
                </div>
              ))}
            </div>
            
            <p className="text-center text-stone-500 text-sm">
              Small batches • Fills up fast
            </p>
          </>
        )}
      </div>
    </section>
  );
});

// ============================================
// VARIANT B: Static CMS Schedule Section
// ============================================
const StaticScheduleSection = memo(function StaticScheduleSection({ cmsData }) {
  const heading = getCMS(cmsData, 'schedule_heading', 'title', 'Class Schedule');
  const subtitle = getCMS(cmsData, 'schedule_heading', 'subtitle', 'Small batches · Limited seats per slot');
  
  // Collect schedule rows from CMS
  const rows = [];
  for (let i = 1; i <= 10; i++) {
    const key = `schedule_row_${i}`;
    if (cmsData[key]) {
      rows.push({
        day: getCMS(cmsData, key, 'title', ''),
        time: getCMS(cmsData, key, 'subtitle', ''),
        className: getCMS(cmsData, key, 'description', ''),
      });
    }
  }

  // WhatsApp for CTA
  const whatsappNumber = getCMS(cmsData, 'whatsapp', 'title', '917075969111').trim().replace(/\D/g, '');
  const whatsappMessage = encodeURIComponent(getCMS(cmsData, 'whatsapp', 'subtitle', 'Hi, I want to book a class'));
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <section className="py-12 px-4 bg-amber-50" data-testid="static-schedule-section">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-5 h-5 text-amber-600" />
          <h2 className="text-xl font-bold text-stone-900">{heading}</h2>
        </div>
        <p className="text-stone-500 text-sm mb-6">{subtitle}</p>
        
        {rows.length > 0 ? (
          <div className="space-y-3 mb-6">
            {rows.map((row, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-4 flex items-center justify-between shadow-sm border border-amber-100"
              >
                <div>
                  <p className="font-semibold text-stone-900">{row.day}</p>
                  <p className="text-sm text-stone-600">{row.time}</p>
                </div>
                <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full">
                  {row.className}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-stone-500">Schedule coming soon</div>
        )}

        <button
          onClick={() => { pushCTAEvent('schedule'); window.open(whatsappUrl, '_blank'); }}
          className="w-full h-14 flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold rounded-xl shadow-lg hover:bg-[#20BD5A] transition-colors"
          data-testid="schedule-whatsapp-cta"
        >
          <WhatsAppIcon className="w-5 h-5" />
          Chat on WhatsApp to Book
        </button>
      </div>
    </section>
  );
});

// Problem Section (Memoized)
const ProblemSection = memo(function ProblemSection({ cmsData }) {
  const heading = getCMS(cmsData, 'problem_section', 'title', 'If your body feels tight, tired or stressed…');
  const subtitle = getCMS(cmsData, 'problem_section', 'subtitle', "You don't need a harder workout");
  const tagline = getCMS(cmsData, 'problem_section', 'description', 'You need something your body responds to');
  const sectionImage = getCMS(cmsData, 'problem_section', 'media_url', '');
  const imageZoom = getZoom(cmsData, 'problem_section');
  const problems = getList(cmsData, 'problem_list', [
    'Back and neck pain',
    'Low energy throughout the day',
    'Constant stress and tension',
    'Keep delaying starting fitness',
  ]);

  return (
    <section className="py-12 px-4 bg-white">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-6">
          {heading}
        </h2>
        
        {/* Section Image from CMS */}
        {sectionImage && (
          <div className="mb-6 rounded-xl overflow-hidden">
            <CmsMedia src={sectionImage} alt={heading} className="w-full h-48 object-cover" zoom={imageZoom} />
          </div>
        )}
        
        <div className="space-y-3 mb-6">
          {problems.map((problem, index) => (
            <div key={index} className="flex items-center gap-3 text-stone-600">
              <div className="w-2 h-2 bg-red-400 rounded-full" />
              <span>{problem}</span>
            </div>
          ))}
        </div>
        
        <div className="bg-stone-50 rounded-xl p-5 border-l-4 border-primary">
          <p className="text-stone-700">
            {subtitle}
          </p>
          <p className="text-primary font-semibold mt-1">
            {tagline}
          </p>
        </div>
      </div>
    </section>
  );
});

// Solution Section - Impact Led (Memoized)
const SolutionSection = memo(function SolutionSection({ cmsData }) {
  const heading = getCMS(cmsData, 'solution_section', 'title', 'In Just 1 Session, You Will:');
  const tagline = getCMS(cmsData, 'solution_section', 'subtitle', 'Designed to make you feel better from day one');
  const benefits = getList(cmsData, 'solution_list', [
    'Stretch deeply without strain',
    'Feel lighter and more relaxed',
    'Improve flexibility and posture',
    'Release stress naturally',
  ]);
  // Get image from CMS (check both solution_section and solution_list)
  const sectionImage = getCMS(cmsData, 'solution_section', 'media_url', '') || getCMS(cmsData, 'solution_list', 'media_url', '');
  const imageZoom = getZoom(cmsData, 'solution_section') !== 100 ? getZoom(cmsData, 'solution_section') : getZoom(cmsData, 'solution_list');

  return (
    <section className="py-12 px-4 bg-primary/5">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-6">
          {heading}
        </h2>
        
        {/* Section Image if provided from CMS */}
        {sectionImage && (
          <div className="mb-6 rounded-xl overflow-hidden">
            <CmsMedia src={sectionImage} alt="Session benefits" className="w-full h-48 object-cover" zoom={imageZoom} />
          </div>
        )}
        
        <div className="space-y-4 mb-6">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-4 h-4 text-white" />
              </div>
              <span className="text-stone-700 text-lg">{benefit}</span>
            </div>
          ))}
        </div>
        
        <p className="text-primary font-medium text-center">
          {tagline}
        </p>
      </div>
    </section>
  );
});

// Beginner Safe Section (Memoized)
const BeginnerSection = memo(function BeginnerSection({ cmsData }) {
  const heading = getCMS(cmsData, 'beginner_section', 'title', 'Made for Complete Beginners');
  const tagline = getCMS(cmsData, 'beginner_section', 'subtitle', 'No pressure. No judgment.');
  const pointsList = getList(cmsData, 'beginner_list', [
    'No experience needed',
    'Step-by-step guidance',
    'Safe and supportive environment',
  ]);
  
  const icons = [Heart, Users, Shield];
  const points = pointsList.map((text, i) => ({
    icon: icons[i] || Heart,
    text
  }));

  return (
    <section className="py-12 px-4 bg-white">
      <div className="max-w-lg mx-auto text-center">
        <h2 className="text-2xl font-bold text-stone-900 mb-6">
          {heading}
        </h2>
        
        <div className="grid grid-cols-3 gap-4 mb-6">
          {points.map(({ icon: Icon, text }, index) => (
            <div key={index} className="flex flex-col items-center">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <span className="text-sm text-stone-600">{text}</span>
            </div>
          ))}
        </div>
        
        <p className="text-stone-500 italic">
          {tagline}
        </p>
      </div>
    </section>
  );
});

// Method Section (Memoized)
const MethodSection = memo(function MethodSection({ cmsData }) {
  const heading = getCMS(cmsData, 'method_section', 'title', 'What Makes This Different');
  const methods = getList(cmsData, 'method_list', [
    'Aerial hammock support',
    'Gentle movements',
    'Breath-based relaxation',
  ]);
  // Check both method_section and method_list for image
  const sectionImage = getCMS(cmsData, 'method_section', 'media_url', '') || getCMS(cmsData, 'method_list', 'media_url', '');
  const imageZoom = getZoom(cmsData, 'method_section') !== 100 ? getZoom(cmsData, 'method_section') : getZoom(cmsData, 'method_list');

  return (
    <section className="py-12 px-4 bg-stone-50">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-6">
          {heading}
        </h2>
        
        {/* Section Image if provided from CMS */}
        {sectionImage && (
          <div className="mb-6 rounded-xl overflow-hidden">
            <CmsMedia src={sectionImage} alt="What makes this different" className="w-full h-48 object-cover" zoom={imageZoom} />
          </div>
        )}
        
        <div className="space-y-3">
          {methods.map((method, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-sm"
            >
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <span className="text-stone-700 font-medium">{method}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

// Testimonials Section (Memoized)
const TestimonialsSection = memo(function TestimonialsSection({ cmsData }) {
  const heading = getCMS(cmsData, 'testimonials_heading', 'title', 'What Women Say');
  
  // Build testimonials from CMS
  const testimonials = useMemo(() => {
    const result = [];
    for (let i = 1; i <= 4; i++) {
      const key = `testimonial_${i}`;
      if (cmsData[key]) {
        result.push({
          text: cmsData[key].title,
          author: cmsData[key].subtitle,
          rating: cmsData[key].metadata?.rating || 5
        });
      }
    }
    // Fallback if no CMS testimonials
    if (result.length === 0) {
      return [
        { text: "My back pain reduced immediately after the first session.", author: "Priya S.", rating: 5 },
        { text: "I was nervous but it felt surprisingly easy and relaxing.", author: "Anjali R.", rating: 5 },
        { text: "I can actually stick to this. Best decision I made!", author: "Meera K.", rating: 5 },
        { text: "I felt lighter in just one class. Amazing experience!", author: "Divya M.", rating: 5 },
      ];
    }
    return result;
  }, [cmsData]);

  return (
    <section className="py-12 px-4 bg-white">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">
          {heading}
        </h2>
        
        <div className="space-y-4">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="overflow-hidden">
              <CardContent className="p-5">
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-stone-700 mb-3">"{testimonial.text}"</p>
                <p className="text-stone-500 text-sm font-medium">— {testimonial.author}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
});

// Video Section - YouTube embed (Memoized)
const VideoSection = memo(function VideoSection({ cmsData }) {
  const videoData = cmsData['video_section'];
  
  if (!videoData?.media_url) return null;
  
  const heading = videoData.title || 'Watch What Happens In Our Sessions';
  const videoUrl = videoData.media_url;
  
  // Convert YouTube URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // Handle YouTube Shorts
    if (url.includes('youtube.com/shorts/')) {
      const videoId = url.split('shorts/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    
    // Handle regular YouTube URLs
    if (url.includes('youtube.com/watch')) {
      const videoId = new URL(url).searchParams.get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    
    // Handle youtu.be short links
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    
    return url; // Return as-is if already embed URL
  };
  
  const embedUrl = getEmbedUrl(videoUrl);
  
  if (!embedUrl) return null;

  return (
    <section className="py-12 px-4 bg-stone-50">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">
          {heading}
        </h2>
        
        <div className="relative rounded-2xl overflow-hidden shadow-lg bg-black aspect-[9/16] max-w-xs mx-auto">
          <iframe
            src={embedUrl}
            title={heading}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
});

// Instructor Bio Section (Memoized)
const InstructorSection = memo(function InstructorSection({ cmsData }) {
  const instructorData = cmsData['instructor_section'];
  
  if (!instructorData) return null;
  
  const heading = instructorData.title || 'Meet Your Instructor';
  const bio = instructorData.description || '';
  const imageUrl = instructorData.media_url;
  const imageZoom = instructorData.metadata?.image_zoom || 100;
  
  // Parse the bio to extract name and description
  const bioLines = bio.split('\n').filter(line => line.trim());
  const instructorName = bioLines[0] || '';
  const bioText = bioLines.slice(1).join('\n').trim();

  return (
    <section className="py-12 px-4 bg-white">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">
          {heading}
        </h2>
        
        <div className="bg-stone-50 rounded-2xl overflow-hidden shadow-sm">
          {/* Instructor Image */}
          {imageUrl && (
            <div className="aspect-[4/3] overflow-hidden">
              <CmsMedia src={imageUrl} alt={instructorName || 'Instructor'} className="w-full h-full object-cover" zoom={imageZoom} />
            </div>
          )}
          
          {/* Instructor Info */}
          <div className="p-6">
            {instructorName && (
              <h3 className="text-xl font-bold text-primary mb-3">
                {instructorName}
              </h3>
            )}
            
            {bioText && (
              <div className="text-stone-600 text-sm leading-relaxed space-y-3">
                {bioText.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph.replace(/\u200b/g, '').trim()}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

// Research Paper Section (Memoized)
const ResearchSection = memo(function ResearchSection({ cmsData }) {
  const researchData = cmsData['research_section'];
  
  if (!researchData) return null;
  
  const heading = researchData.title || 'Research Proves The Benefits';
  const quote = researchData.subtitle || '';
  const linkUrl = researchData.media_url || researchData.link_url; // media_url contains the link in this case

  return (
    <section className="py-12 px-4 bg-primary/5">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">
          {heading}
        </h2>
        
        <div className="bg-white rounded-2xl p-6 shadow-sm border-l-4 border-primary">
          {quote && (
            <blockquote className="text-stone-700 italic mb-4">
              "{quote}"
            </blockquote>
          )}
          
          {linkUrl && (
            <a 
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary font-medium hover:underline text-sm"
            >
              <span>Read the ACE Research Study</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
        
        <p className="text-center text-stone-500 text-xs mt-4">
          Source: American Council on Exercise (ACE)
        </p>
      </div>
    </section>
  );
});

// Value + Offer Repeat (Memoized)
const ValueSection = memo(function ValueSection({ onBookClick, cmsData, abVariant }) {
  const heading = getCMS(cmsData, 'value_heading', 'title', 'What You Get');
  const sectionImage = getCMS(cmsData, 'value_heading', 'media_url', '');
  const imageZoom = getZoom(cmsData, 'value_heading');
  const includes = getList(cmsData, 'value_list', [
    '1 guided session (60 mins)',
    'Personal attention',
    'Women-only environment',
    'Stress relief guaranteed',
  ]);
  
  // Pricing from CMS
  const pricing = cmsData['pricing'] || {};
  const price = pricing.title || '1197';
  const originalPrice = pricing.subtitle || '2000';

  // WhatsApp
  const whatsappNumber = getCMS(cmsData, 'whatsapp', 'title', '917075969111').trim().replace(/\D/g, '');
  const whatsappMessage = encodeURIComponent(getCMS(cmsData, 'whatsapp', 'subtitle', 'Hi, I want to book my first class'));
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;
  const isVariantB = abVariant === 'B';

  return (
    <section className="py-12 px-4 bg-primary/5">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">
          {heading}
        </h2>
        
        {/* Section Image from CMS */}
        {sectionImage && (
          <div className="mb-6 rounded-xl overflow-hidden">
            <CmsMedia src={sectionImage} alt={heading} className="w-full h-48 object-cover" zoom={imageZoom} />
          </div>
        )}
        
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
          <div className="space-y-3 mb-6">
            {includes.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-primary" />
                <span className="text-stone-700">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="border-t pt-6">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="text-center">
                <p className="text-stone-500 text-sm">Today</p>
                <p className="text-3xl font-bold text-primary">₹{price}</p>
              </div>
              <div className="text-center">
                <p className="text-stone-500 text-sm">Regular</p>
                <p className="text-2xl font-bold text-stone-400 line-through">₹{originalPrice}</p>
              </div>
            </div>
            
            {isVariantB ? (
              <button 
                onClick={() => { pushCTAEvent('cta'); window.open(whatsappUrl, '_blank'); }}
                className="w-full h-14 flex items-center justify-center gap-2 text-white font-semibold rounded-xl shadow-lg transition-colors"
                style={{ backgroundColor: '#25D366' }}
                data-testid="value-whatsapp-cta"
              >
                <WhatsAppIcon className="w-5 h-5" />
                Chat on WhatsApp
              </button>
            ) : (
              <Button 
                onClick={onBookClick}
                className="w-full h-14 text-lg font-semibold rounded-xl bg-primary hover:bg-primary/90"
              >
                Book Your First Class
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});

// Location Section (Memoized)
const LocationSection = memo(function LocationSection({ studio, cmsData }) {
  const heading = getCMS(cmsData, 'location', 'title', 'Near You');
  const address = getCMS(cmsData, 'location', 'subtitle', 'SAMA Studio, Financial District');
  const distance = getCMS(cmsData, 'location', 'description', 'Hyderabad • Just 4 KM away');
  
  return (
    <section className="py-12 px-4 bg-white">
      <div className="max-w-lg mx-auto text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="w-6 h-6 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-stone-900 mb-2">
          {heading}
        </h2>
        <p className="text-stone-600 mb-2">{studio?.name || address}</p>
        <p className="text-primary font-medium">{distance}</p>
      </div>
    </section>
  );
});

// Final CTA Section (Memoized)
const FinalCTASection = memo(function FinalCTASection({ onBookClick, cmsData, abVariant }) {
  const finalCTA = cmsData['final_cta'] || {};
  const heading = finalCTA.title || 'Start with just one class';
  const points = (finalCTA.subtitle || 'No commitment|No pressure|Just show up').split('|');
  const scarcityText = finalCTA.description || 'Only 50 discounted spots available. Price goes back to ₹2,000 after.';
  
  // WhatsApp - trim and sanitize number
  const whatsappNumber = getCMS(cmsData, 'whatsapp', 'title', '917075969111').trim().replace(/\D/g, '');
  const whatsappMessage = encodeURIComponent(getCMS(cmsData, 'whatsapp', 'subtitle', 'Hi, I want to book my first aerial yoga class'));
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  // Know More WhatsApp (Variant B)
  const knowMoreNumber = getCMS(cmsData, 'whatsapp_knowmore', 'title', whatsappNumber).trim().replace(/\D/g, '');
  const knowMoreMessage = encodeURIComponent(getCMS(cmsData, 'whatsapp_knowmore', 'subtitle', 'Hi, I want to know more about this class'));
  const knowMoreUrl = `https://wa.me/${knowMoreNumber}?text=${knowMoreMessage}`;
  
  // Phone
  const phone = getCMS(cmsData, 'phone', 'title', '+91 98765 43210');
  const isVariantB = abVariant === 'B';
  
  return (
    <section className="py-16 px-4 bg-gradient-to-b from-primary to-primary/90 text-white">
      <div className="max-w-lg mx-auto text-center">
        <h2 className="text-2xl md:text-3xl font-bold mb-4">
          {heading}
        </h2>
        <div className="space-y-2 mb-8 text-white/80">
          {points.map((point, idx) => (
            <p key={idx} className={idx === points.length - 1 ? "font-semibold text-white" : ""}>
              {point}
            </p>
          ))}
        </div>
        
        {isVariantB ? (
          <>
            <button 
              onClick={() => { pushCTAEvent('cta'); window.open(whatsappUrl, '_blank'); }}
              className="w-full h-14 flex items-center justify-center gap-2 text-white font-semibold rounded-xl shadow-xl mb-4 transition-colors"
              style={{ backgroundColor: '#25D366' }}
              data-testid="final-whatsapp-primary"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Chat on WhatsApp
            </button>
            
            <button 
              onClick={() => { pushCTAEvent('cta'); window.open(knowMoreUrl, '_blank'); }}
              className="w-full h-12 flex items-center justify-center gap-2 text-white/90 font-medium border border-white/30 hover:bg-white/10 rounded-xl transition-colors"
              data-testid="final-whatsapp-secondary"
            >
              <WhatsAppIcon className="w-4 h-4" />
              Know More
            </button>
          </>
        ) : (
          <>
            <Button 
              onClick={onBookClick}
              size="lg"
              className="w-full h-14 text-lg font-semibold rounded-xl bg-white text-primary hover:bg-white/90 shadow-xl mb-4"
            >
              Book Your First Class Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <button 
              onClick={() => { pushCTAEvent('cta'); window.open(whatsappUrl, '_blank'); }}
              className="w-full h-12 flex items-center justify-center gap-2 text-white/90 font-medium hover:bg-white/10 rounded-xl transition-colors"
            >
              <WhatsAppIcon className="w-5 h-5" />
              Chat on WhatsApp
            </button>
          </>
        )}
        
        <a 
          href={`tel:${phone.replace(/\s/g, '')}`}
          className="inline-flex items-center gap-2 text-white/70 mt-4 hover:text-white"
        >
          <Phone className="w-4 h-4" />
          {phone}
        </a>
        
        {/* Final Scarcity */}
        <div className="mt-8 pt-6 border-t border-white/20">
          <p className="text-white/70 text-sm">
            {scarcityText.split('.')[0]}
          </p>
          {scarcityText.split('.')[1] && (
            <p className="text-white/60 text-xs mt-1">
              {scarcityText.split('.')[1]}
            </p>
          )}
        </div>
      </div>
    </section>
  );
});

// =============================================
// NEW SECTIONS: Experience Pass, Urgency, FAQs, Address, Contact, GSTIN
// =============================================

// Location Badge (top of hero)
const LocationBadge = memo(function LocationBadge({ cmsData, studioSlug }) {
  const badges = cmsData?.['location_badges'];
  if (!badges) return null;
  // title = primary location, subtitle = secondary
  const primary = badges.title || '';
  const secondary = badges.subtitle || '';
  const display = studioSlug === 'fd1' ? (secondary || primary) : primary;
  if (!display) return null;
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4" data-testid="location-badge">
      <MapPin className="w-4 h-4 text-primary" />
      <span className="text-sm font-medium text-primary italic">{display}</span>
    </div>
  );
});

// Experience Pass Section
const ExperiencePassSection = memo(function ExperiencePassSection({ cmsData, abVariant }) {
  const passData = cmsData?.['experience_pass'];
  if (!passData) return null;
  const heading = passData.title || "Sama's Pilates Experience Pass";
  const subtitle = passData.subtitle || '';
  const description = passData.description || '';
  const includes = getList(cmsData, 'experience_pass_list', []);
  if (includes.length === 0 && !description) return null;

  const whatsappNumber = getCMS(cmsData, 'whatsapp', 'title', '917075969111').trim().replace(/\D/g, '');
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hi, I want to know more about the Pilates Experience Pass. Can you help me?')}`;

  return (
    <section className="py-12 px-4 bg-gradient-to-b from-primary/5 to-white" data-testid="experience-pass-section">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-3">{heading}</h2>
        {subtitle && <p className="text-lg text-primary font-medium mb-4">{subtitle}</p>}
        {description && <p className="text-stone-600 leading-relaxed mb-6">{description}</p>}

        {includes.length > 0 && (
          <>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">So, What's Included in the Pass?</h3>
            <div className="space-y-3 mb-6">
              {includes.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-stone-700">{item}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {abVariant === 'B' && (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full font-medium hover:bg-[#20bd5a] transition-colors"
            data-testid="experience-pass-whatsapp"
          >
            <WhatsAppIcon className="w-5 h-5" />
            Reserve Your Pass
          </a>
        )}
      </div>
    </section>
  );
});

// Urgency Slots + Countdown
const UrgencySlotsSection = memo(function UrgencySlotsSection({ cmsData }) {
  const slotsData = cmsData?.['urgency_slots'];
  const heading = slotsData?.title || 'Only 6 Passes Left';
  const subtitle = slotsData?.subtitle || '';
  const countdownTarget = slotsData?.description || '';

  const [timeLeft, setTimeLeft] = React.useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  React.useEffect(() => {
    if (!countdownTarget) return;
    const target = new Date(countdownTarget).getTime();
    const tick = () => {
      const now = Date.now();
      const diff = Math.max(0, target - now);
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [countdownTarget]);

  if (!slotsData) return null;

  return (
    <section className="py-12 px-4 bg-stone-900 text-white" data-testid="urgency-slots-section">
      <div className="max-w-lg mx-auto text-center">
        <h2 className="text-2xl font-bold mb-3">{heading}</h2>
        {subtitle && <p className="text-white/70 mb-8">{subtitle}</p>}

        {countdownTarget && (
          <div className="flex justify-center gap-4 mb-6">
            {[
              { val: timeLeft.days, label: 'Days' },
              { val: timeLeft.hours, label: 'Hours' },
              { val: timeLeft.minutes, label: 'Mins' },
              { val: timeLeft.seconds, label: 'Secs' },
            ].map(({ val, label }) => (
              <div key={label} className="flex flex-col items-center">
                <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center text-2xl font-bold">
                  {String(val).padStart(2, '0')}
                </div>
                <span className="text-xs text-white/50 mt-1">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
});

// Post-pass Benefits
const PostPassBenefitsSection = memo(function PostPassBenefitsSection({ cmsData }) {
  const data = cmsData?.['post_pass_benefits'];
  if (!data) return null;
  const benefits = (data.title || '').split('|').filter(Boolean);
  const subtitle = data.subtitle || '';
  if (benefits.length === 0) return null;

  return (
    <section className="py-12 px-4 bg-primary/5" data-testid="post-pass-benefits">
      <div className="max-w-lg mx-auto">
        {subtitle && <p className="text-lg font-medium text-stone-800 mb-6">{subtitle}</p>}
        <div className="space-y-3">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <Heart className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <span className="text-stone-700">{b.trim()}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

// FAQ Section
const FAQSection = memo(function FAQSection({ cmsData }) {
  const [openIndex, setOpenIndex] = React.useState(null);
  const faqs = [];
  for (let i = 1; i <= 10; i++) {
    const faq = cmsData?.[`faq_${i}`];
    if (faq) faqs.push({ question: faq.title, answer: faq.description });
  }
  if (faqs.length === 0) return null;

  return (
    <section className="py-12 px-4 bg-white" data-testid="faq-section">
      <div className="max-w-lg mx-auto">
        <h2 className="text-2xl font-bold text-stone-900 mb-8 text-center">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-stone-200 rounded-xl overflow-hidden" data-testid={`faq-item-${i + 1}`}>
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-stone-50 transition-colors"
              >
                <span className="font-medium text-stone-800 pr-4">{faq.question}</span>
                <ChevronDown className={`w-5 h-5 text-stone-400 shrink-0 transition-transform ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-4 pb-4 text-stone-600 leading-relaxed border-t border-stone-100 pt-3">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
});

// Address + Directions
const AddressSection = memo(function AddressSection({ cmsData }) {
  const addressData = cmsData?.['address_detail'];
  if (!addressData) return null;
  const address = addressData.title || '';
  const mapsUrl = addressData.subtitle || '';

  return (
    <section className="py-10 px-4 bg-stone-50" data-testid="address-section">
      <div className="max-w-lg mx-auto text-center">
        <h3 className="text-sm font-medium text-stone-400 uppercase tracking-wider mb-3">Address</h3>
        <p className="text-stone-700 font-medium mb-4">{address}</p>
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors"
            data-testid="directions-btn"
          >
            <MapPin className="w-4 h-4" />
            Get Directions
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </section>
  );
});

// Contact Section (WhatsApp + Phone + Email)
const ContactSection = memo(function ContactSection({ cmsData }) {
  const contactData = cmsData?.['contact_info'];
  if (!contactData) return null;
  const phone = contactData.title || '';
  const email = contactData.subtitle || '';
  const whatsappNumber = (contactData.description || '').replace(/\D/g, '');

  return (
    <section className="py-10 px-4 bg-white" data-testid="contact-section">
      <div className="max-w-lg mx-auto text-center">
        <h3 className="text-sm font-medium text-stone-400 uppercase tracking-wider mb-4">Get In Touch</h3>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {whatsappNumber && (
            <a
              href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hi, I want to book my first class')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full font-medium hover:bg-[#20bd5a] transition-colors"
              data-testid="contact-whatsapp-btn"
            >
              <WhatsAppIcon className="w-5 h-5" />
              WhatsApp
            </a>
          )}
          {phone && (
            <a href={`tel:${phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 text-stone-700 font-medium hover:text-primary transition-colors" data-testid="contact-phone">
              <Phone className="w-4 h-4" />
              {phone}
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} className="inline-flex items-center gap-2 text-stone-700 font-medium hover:text-primary transition-colors" data-testid="contact-email">
              <Mail className="w-4 h-4" />
              {email}
            </a>
          )}
        </div>
      </div>
    </section>
  );
});

// GSTIN / Business Details
const GSTINSection = memo(function GSTINSection({ cmsData }) {
  const gstinData = cmsData?.['gstin'];
  if (!gstinData) return null;
  const gstin = gstinData.title || '';
  const businessName = gstinData.subtitle || '';

  return (
    <section className="py-6 px-4 bg-stone-100 border-t border-stone-200" data-testid="gstin-section">
      <div className="max-w-lg mx-auto text-center text-xs text-stone-400">
        {businessName && <p className="mb-1">{businessName}</p>}
        {gstin && <p>GSTIN: {gstin}</p>}
      </div>
    </section>
  );
});


// Sticky Mobile CTA (Memoized)
const StickyMobileCTA = memo(function StickyMobileCTA({ onBookClick, visible, cmsData, abVariant }) {
  if (!visible) return null;
  
  // Pricing from CMS
  const pricing = cmsData?.['pricing'] || {};
  const price = pricing.title || '1197';
  const originalPrice = pricing.subtitle || '2000';
  const discount = pricing.description || '40% OFF';
  
  // WhatsApp - trim and sanitize number
  const whatsappNumber = getCMS(cmsData || {}, 'whatsapp', 'title', '917075969111').trim().replace(/\D/g, '');
  const whatsappMessage = encodeURIComponent(getCMS(cmsData || {}, 'whatsapp', 'subtitle', 'Hi, I want to book my first aerial yoga class'));
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  // Know More WhatsApp
  const knowMoreNumber = getCMS(cmsData || {}, 'whatsapp_knowmore', 'title', whatsappNumber).trim().replace(/\D/g, '');
  const knowMoreMessage = encodeURIComponent(getCMS(cmsData || {}, 'whatsapp_knowmore', 'subtitle', 'Hi, I want to know more about this class'));
  const knowMoreUrl = `https://wa.me/${knowMoreNumber}?text=${knowMoreMessage}`;

  const isVariantB = abVariant === 'B';
  
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-2xl p-3 md:hidden safe-area-bottom">
      {isVariantB ? (
        <div className="flex gap-2">
          <button 
            onClick={() => { pushCTAEvent('cta'); window.open(whatsappUrl, '_blank'); }}
            className="flex-1 h-12 flex items-center justify-center gap-2 text-white font-semibold rounded-xl"
            style={{ backgroundColor: '#25D366' }}
            data-testid="sticky-whatsapp-primary"
          >
            <WhatsAppIcon className="w-5 h-5" />
            <span>Chat on WhatsApp</span>
          </button>
          <button 
            onClick={() => { pushCTAEvent('cta'); window.open(knowMoreUrl, '_blank'); }}
            className="w-24 h-12 flex items-center justify-center gap-1.5 border-2 border-stone-200 text-stone-700 font-medium rounded-xl"
            data-testid="sticky-whatsapp-secondary"
          >
            <WhatsAppIcon className="w-4 h-4 text-[#25D366]" />
            <span className="text-sm">More</span>
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button 
            onClick={onBookClick}
            className="flex-1 h-12 text-base font-semibold rounded-xl bg-emerald-700 hover:bg-emerald-800 flex items-center justify-center gap-2"
          >
            <span>Book Now – ₹{price}</span>
            <span className="bg-white/20 text-white text-xs font-bold px-1.5 py-0.5 rounded">
              {discount}
            </span>
          </Button>
          <button 
            onClick={() => { pushCTAEvent('cta'); window.open(whatsappUrl, '_blank'); }}
            className="w-12 h-12 flex items-center justify-center bg-green-500 text-white rounded-xl"
          >
            <WhatsAppIcon className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
});

// Main Landing Page Component
export default function BookFirstClass() {
  const navigate = useNavigate();
  const { studioSlug, categorySlug } = useParams();
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [currentStudio, setCurrentStudio] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  
  // Ref for scrolling to slots section
  const slotsRef = useRef(null);
  
  // Fetch CMS data (page-specific with fallback to default) - instant with cached defaults
  const { cmsData, logoUrl, loading: cmsLoading, pageKey } = useLandingPageCMS(studioSlug, categorySlug);
  useGTM(studioSlug);
  
  // A/B Variant from CMS (admin-controlled toggle)
  const abVariant = getCMS(cmsData, 'ab_variant', 'title', 'A').trim().toUpperCase();
  const isVariantB = abVariant === 'B';

  // Dynamic section ordering from CMS
  const sectionOrder = useSectionOrder(LP_LAYOUT_KEYS.book_first_class, BOOK_FIRST_CLASS_SECTIONS);

  // ===========================================
  // PERFORMANCE: Slots caching
  // ===========================================
  const SLOTS_CACHE_KEY = `sama_slots_${studioSlug || 'all'}_${categorySlug || 'all'}`;
  const SLOTS_CACHE_TTL = 60 * 1000; // 1 minute

  const getCachedSlots = useCallback(() => {
    try {
      const cached = localStorage.getItem(SLOTS_CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < SLOTS_CACHE_TTL) {
          return data;
        }
      }
    } catch (e) {}
    return null;
  }, [SLOTS_CACHE_KEY]);

  const setCachedSlots = useCallback((data) => {
    try {
      localStorage.setItem(SLOTS_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (e) {}
  }, [SLOTS_CACHE_KEY]);

  // Optimized data fetching with caching
  // Variant B uses ONLY CMS static schedule — zero Supabase fetches
  useEffect(() => {
    // Variant B: skip ALL Supabase fetches (sessions, studios)
    if (isVariantB) {
      setIsLoadingSlots(false);
      setAvailableSlots([]);
      return;
    }

    // Also check cached CMS before CMS state settles
    const cachedCms = getCachedVariant(pageKey);
    const cachedAbVariant = cachedCms?.ab_variant?.title?.trim()?.toUpperCase();
    if (cachedAbVariant === 'B') {
      setIsLoadingSlots(false);
      setAvailableSlots([]);
      return;
    }

    // Don't fetch while CMS is still loading (we don't know the variant yet)
    if (cmsLoading) return;

    let cancelled = false;
    
    // Check cache first for instant display
    const cachedSlots = getCachedSlots();
    if (cachedSlots && cachedSlots.length > 0) {
      setAvailableSlots(cachedSlots);
      setIsLoadingSlots(false);
    }
    
    // Fetch fresh data (in background if we have cache)
    async function loadData() {
      if (!cachedSlots || cachedSlots.length === 0) {
        setIsLoadingSlots(true);
      }
      
      try {
        const today = format(new Date(), 'yyyy-MM-dd');
        const [studiosRes, sessionsRes] = await Promise.all([
          fetch(`${API_URL}/api/studios`),
          fetch(`${API_URL}/api/sessions?date=${today}&days=7`)
        ]);
        
        if (cancelled) return;
        
        let studioId = null;
        if (studiosRes.ok) {
          const studios = await studiosRes.json();
          if (studioSlug && studioSlug !== 'all') {
            const match = studios.find(s => 
              (s.code || '').toLowerCase() === studioSlug.toLowerCase()
            );
            if (match) {
              studioId = match.id;
              setCurrentStudio(match);
            }
          }
        }
        
        if (categorySlug && categorySlug !== 'all') {
          setCurrentCategory(categorySlug);
        }
        
        if (sessionsRes.ok) {
          let sessions = await sessionsRes.json();
          
          if (studioId) {
            const studioSessions = sessions.filter(s => s.studio_id === studioId);
            if (studioSessions.length > 0) {
              sessions = studioSessions;
            }
          }
          
          if (categorySlug && categorySlug !== 'all') {
            sessions = sessions.filter(s => {
              const cat = (s.class_type?.category || '').toLowerCase();
              return cat === categorySlug.toLowerCase();
            });
          }
          
          sessions = sessions.filter(s => {
            const available = (s.capacity || 6) - (s.booked_count || 0);
            const isFuture = new Date(s.start_time) > new Date();
            const showOn = s.show_on_landing !== false;
            return available > 0 && isFuture && showOn;
          });
          
          const slots = sessions.slice(0, 4).map(s => {
            const sessionDate = new Date(s.start_time);
            const actual = (s.capacity || 10) - (s.booked_count || 0);
            const options = { timeZone: 'Asia/Kolkata' };
            const dayName = sessionDate.toLocaleDateString('en-US', { ...options, weekday: 'short' });
            const timeStr = sessionDate.toLocaleTimeString('en-US', { 
              ...options, 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            });
            
            return {
              id: s.id,
              sessionId: s.id,
              dayName: dayName,
              time: timeStr,
              className: s.class_type?.name || 'Class',
              seatsLeft: actual,
              displaySeatsLeft: Math.max(1, Math.floor(actual / 2)),
              capacity: s.capacity || 6,
            };
          });
          
          if (!cancelled) {
            setAvailableSlots(slots);
            setCachedSlots(slots);
          }
        }
      } catch (err) {
        console.error('Slots fetch error:', err);
      } finally {
        if (!cancelled) setIsLoadingSlots(false);
      }
    }
    
    loadData();
    return () => { cancelled = true; };
  }, [studioSlug, categorySlug, getCachedSlots, setCachedSlots, pageKey, isVariantB, cmsLoading]);

  // Scroll listener for sticky CTA
  useEffect(() => {
    const onScroll = () => setShowStickyCTA(window.scrollY > 500);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Handle slot selection - navigate to booking page (contained)
  const handleSelectSlot = (slot) => {
    setSelectedSlot(slot);
    // Navigate directly to booking with session ID
    navigate(`/book?session=${slot.sessionId}`);
  };

  // Handle generic book click - scroll to slots section (stay on LP)
  const handleBookClick = useCallback(() => {
    pushCTAEvent('book');
    if (slotsRef.current) {
      slotsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">
      {/* Logo Header - Static branding, no navigation */}
      <LogoHeader logoUrl={logoUrl} />
      
      {/* Show loading state for non-default variants until CMS data arrives */}
      {cmsLoading ? (
        <div className="pt-16 flex flex-col items-center justify-center min-h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#2F5D50] mb-4" />
          <p className="text-stone-400 text-sm">Loading...</p>
        </div>
      ) : (
      <>
      {/* Add top padding to account for fixed header */}
      <div className="pt-16">
        <HeroSection 
          onBookClick={handleBookClick} 
          category={currentCategory}
          studio={currentStudio}
          cmsData={cmsData}
          abVariant={abVariant}
        />
        {/* Dynamically ordered sections */}
        {(() => {
          const sectionMap = {
            slots: (
              <div key="slots" ref={slotsRef}>
                {isVariantB ? (
                  <StaticScheduleSection cmsData={cmsData} />
                ) : (
                  <UrgencySection 
                    studioSlug={studioSlug}
                    categorySlug={categorySlug}
                    onSelectSlot={handleSelectSlot}
                    slots={availableSlots}
                    isLoading={isLoadingSlots}
                  />
                )}
              </div>
            ),
            problem: <ProblemSection key="problem" cmsData={cmsData} />,
            solution: <SolutionSection key="solution" cmsData={cmsData} />,
            beginner: <BeginnerSection key="beginner" cmsData={cmsData} />,
            method: <MethodSection key="method" cmsData={cmsData} />,
            testimonials: <TestimonialsSection key="testimonials" cmsData={cmsData} />,
            video: <VideoSection key="video" cmsData={cmsData} />,
            instructor: <InstructorSection key="instructor" cmsData={cmsData} />,
            research: <ResearchSection key="research" cmsData={cmsData} />,
            value: <ValueSection key="value" onBookClick={handleBookClick} cmsData={cmsData} abVariant={abVariant} />,
            experience_pass: <ExperiencePassSection key="experience_pass" cmsData={cmsData} abVariant={abVariant} />,
            post_pass_benefits: <PostPassBenefitsSection key="post_pass_benefits" cmsData={cmsData} />,
            urgency_slots: <UrgencySlotsSection key="urgency_slots" cmsData={cmsData} />,
            location: <LocationSection key="location" studio={currentStudio} cmsData={cmsData} />,
            final_cta: <FinalCTASection key="final_cta" onBookClick={handleBookClick} cmsData={cmsData} abVariant={abVariant} />,
            faq: <FAQSection key="faq" cmsData={cmsData} />,
            address: <AddressSection key="address" cmsData={cmsData} />,
            contact: <ContactSection key="contact" cmsData={cmsData} />,
            gstin: <GSTINSection key="gstin" cmsData={cmsData} />,
          };
          return sectionOrder.map(s => s.visible !== false ? sectionMap[s.id] : null);
        })()}
      </div>
      
      {/* Sticky Mobile CTA */}
      <StickyMobileCTA 
        onBookClick={handleBookClick} 
        visible={showStickyCTA}
        cmsData={cmsData}
        abVariant={abVariant}
      />
      </>
      )}
    </div>
  );
}
